import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import { useDispatch, useSelector } from "react-redux";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import { RootState } from "../../../store";
import { toast } from "react-toastify";
const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface MeetingData {
  bp_lgd_code: number;
  bp_name: string;
  total_meetings_uploaded: number;
  draft_mom: number;
  final_submitted_mom: number;
}

const BlockDashboard3: React.FC<{ finYear: string; meetingType: any }> = ({
  finYear,
  meetingType,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<MeetingData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const blockData = useSelector((state: RootState) => state.common.dashboardData["Block Panchayats"]);

  useEffect(() => {
    const fetchBlocks = async () => {
      setTableData([]);
      if (!blockData?.state_code || !blockData?.zp_lgd_code) return;
      try {
        const response = await axios.get(`${baseUrl}api/bp_report/${blockData.state_code}/${blockData.zp_lgd_code}`, {
          params: { fin_year: finYear, meeting_type: meetingType.value !== "0" ? meetingType.value : undefined },
        });
        if (response?.data?.msg_code === 400) {
          toast.error(response?.data?.msg || "Something went wrong");
          return;
        }
        const mappedData: MeetingData[] = response?.data?.data?.map((item: any) => ({
          bp_lgd_code: item.bp_lgd_code,
          bp_name: item.bp_name,
          total_meetings_uploaded: item.total_meetings_uploaded,
          draft_mom: item.draft_mom || 0,
          final_submitted_mom: item.final_submitted_mom || 0,
        })) || [];
        setTableData(mappedData);
      } catch (error) {
        console.error("Error fetching block data:", error);
      }
    };
    fetchBlocks();
  }, [finYear, meetingType, baseUrl, blockData?.state_code, blockData?.zp_lgd_code]);

  const handleMomPage = (bpCode: number, bpName: string) => {
    dispatch(updateDashboardFields({ menu: "Block Panchayats", fields: { bp_lgd_code: bpCode, bp_name: bpName } }));
  };

  const handleBack = () => {
    dispatch(updateDashboardFields({ menu: "Block Panchayats", fields: { zp_lgd_code: null, zp_name: "", bp_lgd_code: null, bp_name: "" } }));
  };

  const columns: TableColumn<MeetingData>[] = [
    { name: "S.No.", cell: (_row, index) => (index + 1 + (currentPage - 1) * rowsPerPage).toString(), width: "80px" },
    {
      name: "BP Name",
      selector: (row) => row.bp_name,
      cell: (row) => (
        <span className={row.total_meetings_uploaded > 0 ? "highlight-td" : ""}
          style={{ cursor: row.total_meetings_uploaded > 0 ? "pointer" : "default" }}
          onClick={() => row.total_meetings_uploaded > 0 && handleMomPage(row.bp_lgd_code, row.bp_name)}>
          {row.bp_name}
        </span>
      ), sortable: true,
    },
    { name: "Draft MoM", selector: (row) => row.draft_mom, sortable: true },
    { name: "Final Submitted", selector: (row) => row.final_submitted_mom, sortable: true },
    { name: "Total Meetings Uploaded", selector: (row) => row.total_meetings_uploaded, sortable: true },
  ];

  const filteredData = tableData.filter((item) => item.bp_name?.toLowerCase().includes(filterText?.toLowerCase()) || String(item.bp_lgd_code).includes(filterText));

  const t = {
    draft: filteredData.reduce((s, r) => s + r.draft_mom, 0),
    final: filteredData.reduce((s, r) => s + r.final_submitted_mom, 0),
    total: filteredData.reduce((s, r) => s + r.total_meetings_uploaded, 0),
  };

  const exportExcel = () => {
    const header = ["S.No.", "BP Name", "Draft MoM", "Final Submitted", "Total Meetings Uploaded"];
    const data = filteredData.map((r, i) => [i + 1, r.bp_name, r.draft_mom, r.final_submitted_mom, r.total_meetings_uploaded]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data, ["", "Total", t.draft, t.final, t.total]]);
    ws["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 25 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BPReport");
    XLSX.writeFile(wb, "BPReport.xlsx");
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between mb-2">
         <div className="d-flex gap-2"> 
          <button className="back-btn me-2" onClick={handleBack}>&larr; Back</button>
          <div className="data-chip me-2">{blockData?.state_name} - {blockData?.state_code}</div>
          <div className="data-chip me-2">{blockData?.zp_name} - {blockData?.zp_lgd_code}</div>
        </div>
        <button className="shiny-btn" onClick={exportExcel}><FaFileExcel /> Export Excel</button>
      </div>
      <input type="text" className="form-control w-25 mb-2" placeholder="Search..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
      <DataTable columns={columns} data={filteredData} pagination onChangePage={setCurrentPage} onChangeRowsPerPage={setRowsPerPage} highlightOnHover striped />
      {filteredData.length > 0 && (
        <div className="table-footer fw-bold bg-light d-flex p-2 border">
          <div style={{ width: "80px" }}></div>
          <div style={{ flex: 1 }}>Total</div>
          <div style={{ flex: 1 }}>{t.draft}</div>
          <div style={{ flex: 1 }}>{t.final}</div>
          <div style={{ flex: 1 }}>{t.total}</div>
        </div>
      )}
    </div>
  );
};

export default BlockDashboard3;